import GeneralUtility from '../utilities/general-utility';
import moment from 'moment';
import LanguageUtility from '../utilities/language-utility';

/**
 * Contains helper function for managing forms.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class FormHelpers {
    /**
     * Contains changed values of all forms, that should trigger an alert-message when the user tries to exit the page with unsaved data.
     */
    private static changedForms: { [key: string]: any } = {};

    /**
     * Adds event listeners to all forms, that check if a form has changed any of its values or not. Will be used by FormHelpers::checkIfFormsChanged to show
     * an alert-message to the user, if a form has been edited, and the page is being exited.
     */
    public static initializeUnsavedFormsCheck(): void {
        document.querySelectorAll<HTMLFormElement>('form[data-unsaved-check="1"]').forEach(form => {
            const formName = form.getAttribute('name');
            if (!formName) {
                return;
            }

            // Setup an object storage for each form, to accumulate changed input values
            const changedInputElements: { [key: string]: any } = {};

            form.querySelectorAll<HTMLInputElement>('input:not([data-ignore]),select:not([data-ignore]),textarea:not([data-ignore])').forEach(inputElement => {
                const inputElementName = inputElement.getAttribute('name');
                const originalValue = inputElement.value;
                if (!inputElementName || !originalValue) {
                    return;
                }

                // Each input element is initialized with "no changes present (false)". The input-event-listener will set this to "has changed (true)", if
                // applicable.
                changedInputElements[inputElementName] = false;
                inputElement.addEventListener('input', () => {
                    // Value differs from the original value => consider input as changed
                    if (originalValue !== inputElement.value) {
                        changedInputElements[inputElementName] = true;
                    }
                    // Input has already been marked as changed, but the value has been reverted to the original value => consider as not changed anymore
                    else if (changedInputElements[inputElementName] && originalValue === inputElement.value) {
                        changedInputElements[inputElementName] = false;
                    }
                });
            });

            FormHelpers.changedForms[formName] = changedInputElements;

            // Unset any has-changed values, if the form has been submitted
            form.addEventListener('submit', () => {
                delete FormHelpers.changedForms[formName];
            });
        });
    }

    /**
     * Looks through the changed values as created by the event listeners of FormHelpers::initializeUnsavedFormsCheck.
     *
     * @returns {boolean} True when there is at least one changed input element, otherwise false
     */
    public static checkIfUnsavedFormsPresent(): boolean {
        for (const formName in FormHelpers.changedForms) {
            for (const changedInputElementName in FormHelpers.changedForms[formName]) {
                const hasChanged = FormHelpers.changedForms[formName][changedInputElementName];
                if (hasChanged) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Sanitizes a string to be a (probably) valid float value.
     *
     * @param {string} value Value to sanitize
     * @returns {string} Sanitized value
     */
    public static sanitizeFloat(value: string): string {
        return value.replace(',', '.');
    }

    constructor() {
        this.initializeScrollToForms();
        this.initializeDateFields();
        this.initializeTextareaFields();
        this.initializeSanitization();
    }

    /**
     * Initializes automatic scrolling to sent forms. A form needs to set a URL hash in the form "form-[formId]" for this to work.
     */
    private initializeScrollToForms(): void {
        const hash = window.location.hash;
        if (!hash || hash.indexOf('#form-') === -1) {
            return;
        }
        const formId = hash.replace('#form-', '');
        const form = document.querySelector<HTMLFormElement>('form#' + formId);
        if (!form) {
            return;
        }

        const position = GeneralUtility.getElementPosition(form);
        window.scrollTo({
            top: position.top > 0 ? position.top - 250 : 0,
            behavior: 'smooth'
        });
    }

    /**
     * Initializes all date pickers and ranges, using simple text input fields for the dates.
     */
    private initializeDateFields(): void {
        const nowMoment = moment().startOf('day');
        const nowTimestamp = nowMoment.format('X');
        const defaultToMoment = nowMoment.clone().add(1, 'months').startOf('day');
        const defaultToTimestamp = defaultToMoment.format('X');

        // Validates the date fields on change; make sure to also apply Joi validation, to show a error message to the user
        const validateFields = (fromField: HTMLInputElement, fromHiddenField: HTMLInputElement, toField: HTMLInputElement, toHiddenField: HTMLInputElement) => {
            const fromMoment = moment(fromField.value, 'DD.MM.YYYY');
            const toMoment = moment(toField.value, 'DD.MM.YYYY');

            if (fromMoment.isValid()) {
                fromHiddenField.value = fromMoment.startOf('day').format('X');
            } else {
                fromHiddenField.value = '';
            }

            if (toMoment.isValid()) {
                toHiddenField.value = toMoment.startOf('day').format('X');
            } else {
                toHiddenField.value = '';
            }

            if (fromMoment.isValid() && toMoment.isValid() && fromMoment >= toMoment) {
                fromHiddenField.value = '';
                toHiddenField.value = '';
            }
        };

        // Sets a default date for when the date fields are shown initially
        const setDefaultDate = (type: string, field: HTMLInputElement, hiddenField: HTMLInputElement) => {
            // Check if a default date option if given
            if (field.dataset.dateDefault) {
                // If a default date is enabled, try to parse the given date, otherwise no default date will be set
                if (field.dataset.dateDefault !== '0') {
                    const date = moment(field.dataset.dateDefault);
                    if (date.isValid()) {
                        field.value = date.format('DD.MM.YYYY');
                        hiddenField.value = date.toString();
                    }
                }
            }
            // No default date option present; initialize with now
            else {
                if (type === 'to') {
                    field.value = defaultToMoment.format('DD.MM.YYYY');
                    hiddenField.value = defaultToTimestamp.toString();
                } else {
                    field.value = nowMoment.format('DD.MM.YYYY');
                    hiddenField.value = nowTimestamp.toString();
                }
            }
        };

        // Initialize standalone date fields
        document.querySelectorAll<HTMLInputElement>('.date-field:not(.range-from,.range-to)').forEach(inputField => {
            if (!inputField.dataset.target) {
                return;
            }
            const hiddenField = document.querySelector<HTMLInputElement>(inputField.dataset.target);
            if (!hiddenField) {
                return;
            }

            // Set saved value
            if (hiddenField.value) {
                const fieldMoment = moment.unix(parseInt(hiddenField.value)).startOf('day');
                inputField.value = fieldMoment.format('DD.MM.YYYY');
            }

            // Set hidden field value on change
            inputField.addEventListener('change', () => {
                const dateMoment = moment(inputField.value, 'DD.MM.YYYY');

                // Invalid date entered
                // Note that the field showing the date to the user is not reset. Joi form validation should be set up, to apply client side validation.
                if (!dateMoment.isValid()) {
                    hiddenField.value = '';
                } else {
                    hiddenField.value = dateMoment.startOf('day').format('X');
                }
            });
        });

        // Initialize date range fields
        document.querySelectorAll<HTMLElement>('.date-range-fields').forEach(rangeContainer => {
            const fromField = rangeContainer.querySelector<HTMLInputElement>('.range-from');
            const toField = rangeContainer.querySelector<HTMLInputElement>('.range-to');
            if (!fromField || !toField || !fromField.dataset.target || !toField.dataset.target) {
                return;
            }
            const fromHiddenField = document.querySelector<HTMLInputElement>(fromField.dataset.target);
            const toHiddenField = document.querySelector<HTMLInputElement>(toField.dataset.target);
            if (!fromHiddenField || !toHiddenField) {
                return;
            }

            // Set saved values, or initialize with default values
            if (fromHiddenField.value) {
                const fromMoment = moment.unix(parseInt(fromHiddenField.value)).startOf('day');
                fromField.value = fromMoment.format('DD.MM.YYYY');
            } else {
                setDefaultDate('from', fromField, fromHiddenField);
            }

            if (toHiddenField.value) {
                const toMoment = moment.unix(parseInt(toHiddenField.value)).startOf('day');
                toField.value = toMoment.format('DD.MM.YYYY');
            } else {
                setDefaultDate('to', toField, toHiddenField);
            }

            // Parse and set values on change
            fromField.addEventListener('change', () => {
                validateFields(fromField, fromHiddenField, toField, toHiddenField);
            });

            toField.addEventListener('change', () => {
                validateFields(fromField, fromHiddenField, toField, toHiddenField);
            });
        });
    }

    /**
     * Initializes all textarea fields with maxlength attribute to show remaining characters.
     */
    private initializeTextareaFields(): void {
        const label = LanguageUtility.getLabel('symbolsLeft');

        document.querySelectorAll<HTMLTextAreaElement>('textarea[maxlength]').forEach(textarea => {
            let maxLength = 0;
            const maxlengthAttribute = textarea.getAttribute('maxlength');
            if (textarea.dataset.validateMax) {
                maxLength = parseInt(textarea.dataset.validateMax);
            } else if (maxlengthAttribute) {
                maxLength = parseInt(maxlengthAttribute);
            }
            if (maxLength === 0) {
                return;
            }

            const infoElement = document.createElement('span');
            infoElement.classList.add('form-text');

            const remainingElement = document.createElement('span');
            remainingElement.classList.add('remaining');
            remainingElement.textContent = maxLength.toString();

            infoElement.append(remainingElement);
            infoElement.insertAdjacentText('beforeend', ' / ' + maxLength + ' ' + label);
            textarea.insertAdjacentElement('afterend', infoElement);

            textarea.addEventListener('change', () => {
                const remainingCharacters = maxLength - textarea.value.length;
                remainingElement.textContent = remainingCharacters.toString();
            });

            textarea.addEventListener('keyup', () => {
                const remainingCharacters = maxLength - textarea.value.length;
                remainingElement.textContent = remainingCharacters.toString();
            });

            const remainingCharacters = maxLength - textarea.value.length;
            remainingElement.textContent = remainingCharacters.toString();
        });
    }

    /**
     * Initializes sanitizing of form values. Note that most validation should be done via Joi. The sanitizations applied here add some additional parsing on
     * top of Joi.
     */
    private initializeSanitization(): void {
        document.querySelectorAll<HTMLInputElement>('[data-sanitize]').forEach(field => {
            if (!field.dataset.sanitize) {
                return;
            }

            field.addEventListener('blur', () => {
                if (field.value) {
                    const value = field.value.trim();
                    switch (field.dataset.sanitize) {
                        case 'float':
                            field.value = FormHelpers.sanitizeFloat(value);
                            break;
                    }
                }
            });
        });
    }
}
